// Per-trace drill-down list for the selected day.

import { Link } from "react-router-dom";

import type { SpendTraceRow } from "@/services/api/spend";
import { cn } from "@/utils/cn";

interface Props {
  day: string;
  traces: SpendTraceRow[];
  isLoading: boolean;
}

// The shared state badges from index.css. Same triple the design spec assigns
// to matrix coverage — good = verdigris, hard failure = oxblood-500, degraded =
// oxblood-400 — so a run status and a coverage gap read the same way.
const STATUS_COLORS: Record<string, string> = {
  succeeded: "badge-covered",
  failed: "badge-gap",
  capped: "badge-partial",
};

const ROUTE_LABELS: Record<string, string> = {
  lookup: "Lookup",
  analysis: "Analysis",
  deep_work: "Deep Work",
};

export function TraceList({ day, traces, isLoading }: Props) {
  if (isLoading) {
    return <p className="px-3 py-6 text-sm italic text-warmgray-400">Loading…</p>;
  }
  if (traces.length === 0) {
    return (
      <p className="rounded border border-ruleline bg-parchment-50 px-4 py-6 text-sm italic text-warmgray-500">
        No traces recorded on {day}.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-ruleline">
      <table className="w-full text-sm">
        <thead className="bg-parchment-100 text-left text-[0.65rem] uppercase tracking-wider text-warmgray-500">
          <tr>
            <th className="px-3 py-2">Time</th>
            <th className="px-3 py-2">Class</th>
            <th className="px-3 py-2 text-right">Tools</th>
            <th className="px-3 py-2 text-right">Tokens</th>
            <th className="px-3 py-2 text-right">Cost</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Session</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ruleline bg-parchment-50">
          {traces.map((t) => {
            const time = new Date(t.ended_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
            const cost = parseFloat(t.cost_usd).toFixed(4);
            const statusClass = STATUS_COLORS[t.status] ?? "badge-state bg-parchment-200 text-warmgray-500";
            return (
              <tr key={t.trace_id} className="text-espresso-700">
                <td className="px-3 py-2 font-mono text-xs">{time}</td>
                <td className="px-3 py-2 text-xs">
                  {ROUTE_LABELS[t.route_class] ?? t.route_class}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{t.tool_calls}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {t.total_tokens.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">${cost}</td>
                <td className="px-3 py-2">
                  <span className={cn(statusClass)}>{t.status}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {t.session_id ? (
                    <Link
                      to={`/history/${t.session_id}`}
                      className="text-oxblood-500 hover:underline"
                    >
                      Open
                    </Link>
                  ) : (
                    <span className="text-warmgray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
